import { useState } from "react";
import { toast } from "sonner";
import { ScanLine, Loader2, CheckCircle2, AlertTriangle, Shrink } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { FillPagesButton } from "./FillPagesButton";
import { OpenerFixButton } from "./OpenerFixButton";
import { AcceptAllButton } from "./AcceptAllButton";
import { AllSpreadsButton } from "./AllSpreadsButton";
import { PagePlan } from "./PagePlan";
import { StylePanel } from "./StylePanel";

const STATUS = {
  pass: ["Pass", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  light: ["Under 85%", "bg-amber-50 text-amber-800 border-amber-200"],
  overflow: ["Spills over", "bg-red-50 text-red-700 border-red-200"],
  skip: ["Not measured", "bg-stone-50 text-stone-500 border-stone-200"],
  accepted: ["Accepted as is", "bg-sky-50 text-sky-700 border-sky-200"],
  two_page: ["2 pages allowed", "bg-sky-50 text-sky-700 border-sky-200"],
};

const TwoBtn = ({ r, kitId, reload }) => {
  const on = r.status === "two_page";
  const go = async (e) => {
    e.stopPropagation();
    try { await http.post(`/kits/${kitId}/page-two`, { block: r.block, on: !on }); toast.success(on ? "Back to needing one page" : "Allowed to run over 2 pages — the second page has no fill rule"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return <button data-testid={`page-two-${r.n}`} onClick={go} className="ml-2 text-[11px] font-semibold underline text-sky-700 hover:text-sky-900">{on ? "Undo" : "Allow 2 pages"}</button>;
};

const FitBtn = ({ r, kitId, reload }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await http.post(`/kits/${kitId}/fit-page`, { block: r.block }); toast.success(`Fitting “${r.label}” onto one page — follow it in Live activity at the top`); reload(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <button data-testid={`page-fit-${r.n}`} onClick={go} disabled={busy}
      className="ml-2 inline-flex items-center gap-1 rounded-sm bg-red-700 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-red-800 transition-colors disabled:opacity-50">
      {busy && <Loader2 size={11} className="animate-spin" />} Fit to 1 page
    </button>
  );
};

const FitAllButton = ({ kit, reload, disabled }) => {
  const [busy, setBusy] = useState(false);
  const n = new Set((kit.page_check?.pages || []).filter((r) => ["overflow", "two_page"].includes(r.status)).map((r) => r.block)).size;
  if (!n) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await http.post(`/kits/${kit.id}/fit-all-spills`); toast.success(`Fitting ${data.count} spilling page(s) onto one page — follow it in Live activity at the top`); reload(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <button data-testid="fit-all-spills-button" onClick={go} disabled={disabled || busy || kit.filling}
      className="inline-flex items-center gap-2 rounded-sm bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800 transition-colors disabled:opacity-40">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Shrink size={15} />} Fit all {n} spill{n === 1 ? "" : "s"} to 1 page
    </button>
  );
};

const AcceptBtn = ({ r, kitId, reload }) => {
  const on = r.status === "accepted";
  const go = async (e) => {
    e.stopPropagation();
    try { await http.post(`/kits/${kitId}/page-accept`, { block: r.block, accept: !on }); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return <button data-testid={`page-accept-${r.n}`} onClick={go} className="ml-2 text-[11px] underline text-stone-600 hover:text-[#2B2A26]">{on ? "Undo" : "Accept as is"}</button>;
};

const PageRow = ({ r, onOpen, kitId, reload, first }) => {
  const [lbl, cls] = STATUS[r.status];
  const pct = Math.round(r.fill * 100);
  return (
    <tr data-testid={`page-check-row-${r.n}`} onClick={() => onOpen?.(r.n - 1)} title="Open this page in the PDF preview"
      className="group border-t border-stone-100 cursor-pointer hover:bg-[#C9A847]/5 transition-colors">
      <td className="py-2 pr-3 font-mono-ui text-xs text-stone-500">{r.n}</td>
      <td className="py-2 pr-3 text-sm text-[#2B2A26]">{r.label}<span className="ml-2 text-[11px] text-[#9A7B2C] opacity-0 group-hover:opacity-100">Open page →</span></td>
      <td className="py-2 pr-3 w-48">
        {r.status !== "skip" && <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 bg-stone-100 rounded-full overflow-hidden"><div className={`h-full ${pct >= 85 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
          <span className="font-mono-ui text-xs text-stone-600 w-9 text-right">{pct}%</span>
        </div>}
      </td>
      <td className="py-2"><span data-testid={`page-check-status-${r.n}`} className={`text-[11px] border rounded-sm px-2 py-0.5 ${cls}`}>{lbl}</span>{(r.status === "accepted" || r.status === "light") && <AcceptBtn r={r} kitId={kitId} reload={reload} />}{r.status === "two_page" && first && <TwoBtn r={r} kitId={kitId} reload={reload} />}{r.status === "overflow" && first && <><FitBtn r={r} kitId={kitId} reload={reload} /><TwoBtn r={r} kitId={kitId} reload={reload} /></>}</td>
    </tr>
  );
};

const Summary = ({ pc, st }) => (
  <div data-testid="page-check-summary" className={`flex items-center gap-2 text-sm ${!st.fresh ? "text-stone-500" : st.fails ? "text-amber-800" : "text-emerald-700"}`}>
    {st.fresh && !st.fails ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
    {!pc ? "Not checked yet — run the page check before publishing."
      : !st.fresh ? "The kit has changed since the last check — run it again before publishing."
      : st.fails ? `${st.light} page(s) under 85% · ${st.overflow} block(s) spilling onto an extra page — publishing is blocked until these pass.`
      : `All ${pc.total} pages pass${pc.pages?.some((r) => r.status === "accepted") ? ` (${pc.pages.filter((r) => r.status === "accepted").length} accepted as is)` : ""} — ready to publish.`}
  </div>
);

export const PageCheckTab = ({ kit, reload, disabled, onOpen, problems, setProblems }) => {
  const [starting, setStarting] = useState(false);
  const pc = kit.page_check;
  const st = kit.summary?.page_check || {};
  const running = starting || kit.page_checking;
  const rows = (pc?.pages || []).filter((r) => !problems || ["light", "overflow", "accepted", "two_page"].includes(r.status));

  const run = async () => {
    setStarting(true);
    try {
      await http.post(`/kits/${kit.id}/page-check`);
      toast.message("Checking every page — about a minute for a large kit");
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setStarting(false);
  };

  return (
    <div data-testid="page-check-tab" className="rounded-xl border border-stone-200 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-2xl text-[#2B2A26]">Page check</h2>
          <Summary pc={pc} st={st} />
          {kit.fill_result?.error && !kit.filling && <div data-testid="fill-error" className="text-xs text-red-600 mt-1">Last Fill pages run: {kit.fill_result.error}</div>}
          {kit.page_check_error && <div data-testid="page-check-error" className="text-xs text-red-600 mt-1">Last check failed: {kit.page_check_error}</div>}
          {pc?.at && <div data-testid="page-check-time" className="text-xs text-stone-400 mt-1">Last checked {new Date(pc.at).toLocaleString()}</div>}
          {pc?.estimated && <div data-testid="page-check-estimated" className="text-xs text-sky-700 mt-1">Quick check — page numbers are approximate until the full PDF finishes building in the background.</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="run-page-check-button" onClick={run} disabled={disabled || running || kit.filling}
            className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] px-4 py-2 text-sm font-bold text-white hover:bg-[#B8973A] transition-colors disabled:opacity-40">
            {running ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />} {running ? "Checking pages…" : "Run page check"}
          </button>
          {st.fresh && <AcceptAllButton kit={kit} reload={reload} disabled={running} />}
          {st.fresh && <AllSpreadsButton kit={kit} reload={reload} disabled={running} />}
          <FillPagesButton kit={kit} reload={reload} disabled={disabled || running} />
          <FitAllButton kit={kit} reload={reload} disabled={disabled || running} />
          {kit.collection === "founders" && <OpenerFixButton kit={kit} reload={reload} disabled={disabled || running} />}
        </div>
      </div>
      {pc?.pages && <>
        <StylePanel kit={kit} onOpen={onOpen} reloadKit={reload} />
        {st.fresh && <PagePlan kit={kit} reload={reload} disabled={disabled || running} />}
        <label className="flex items-center gap-2 text-xs text-stone-600 mb-2">
          <input data-testid="page-check-problems-only" type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} /> Show problem pages only
        </label>
        {rows.length ? <table data-testid="page-check-table" className="w-full text-left">
          <thead><tr className="text-[11px] uppercase tracking-wider text-stone-400"><th className="pb-2 pr-3">Page</th><th className="pb-2 pr-3">What's on it</th><th className="pb-2 pr-3">Fill</th><th className="pb-2">Result</th></tr></thead>
          <tbody>{rows.map((r, i) => <PageRow key={r.n} r={r} onOpen={onOpen} kitId={kit.id} reload={reload} first={!rows.slice(0, i).some((x) => x.block === r.block && ["overflow", "two_page"].includes(x.status))} />)}</tbody>
        </table> : <div data-testid="page-check-empty" className="text-sm text-stone-500">No problem pages.</div>}
      </>}
    </div>
  );
};
